
import React, { useContext, useEffect, useState } from "react";

import { Preview } from "@creatomate/preview";
import { makePost } from "@culturehq/client";
import { Button, Icon, Tooltip, classnames, Dropdown } from "@culturehq/components";

import { ElementSource, Subtitle } from "chq";
import { Trans, errorService } from "app/utils";

import useAddTags from "components/stories/post-stories-v2/hooks/useAddTags";
import { CreatomateContext } from "../../contexts/VideoStudioContext";
import { isSubtitleText, updateSubtitlesText } from "../../sidebar/tabs/subtitle/utils";
import { getCreatomateMediaData } from "../../utils/creatomate/utils";
import { VIDEO_TYPE } from "../../utils/constants";
import { StoryModalContext } from "../../contexts/StoryContext";

interface DownloadDropdownProps {
  onClose: () => void;
  preview: Preview;
}

const SaveDropdownElement: React.FC<DownloadDropdownProps> = ({
  onClose, preview
}) => {
  const {
    data,
    handleUpdateData,
    handleAutosaveDraft,
    restartData
  } = useContext(CreatomateContext);
  const { selectedBody, selectedChannel, selectedOptions } = useContext(StoryModalContext);
  const { ready } = preview;
  const {
    burnSubtitles,
    loading,
    mediaType,
    montage,
    onMediaEdited,
    onPosted,
    onThirdPartyMediasReload,
    pendingSubtitle,
    selectedMediaVersion,
    selectedTemplate,
    selectedThumbnail,
    story,
    storyQuestionId,
    uploadingMedia
  } = data;
  const elements = preview.state?.elements || [];

  const [btnLabel, setBtnLabel] = useState("Save Changes");

  useEffect(
    () => {
      if (story?.id) {
        setBtnLabel(mediaType === VIDEO_TYPE ? "Update Video" : "Update Image");
      }
    }, [story, mediaType]
  );

  const addTags = useAddTags({ tags: selectedOptions });

  const callProcessMedia = async (body: Record<string, any>) => makePost("/stories/process_media", body);

  const postEdition = async (mediaData: ElementSource) => {
    let body = {
      storyId: story?.id,
      mediaData,
      burnSubtitles,
      removeSubtitles: selectedMediaVersion?.original,
      templateId: selectedTemplate?.value
    } as Record<string, any>;

    if (selectedThumbnail) {
      body = { ...body, thumbnail: selectedThumbnail };
    } else if (selectedMediaVersion?.thumbnail) {
      body = { ...body, thumbnail: selectedMediaVersion?.thumbnail };
    }

    await callProcessMedia(body);
  };

  const saveMontage = async (): Promise<void> => {
    const params: Record<string, any> = {
      storyQuestionId,
      body: addTags(selectedBody),
      slackChannel: selectedChannel,
      mediaData: getCreatomateMediaData(preview, burnSubtitles)
    };
    await makePost("/stories/generate_montage", params);

    restartData();
    preview.dispose();
    onClose();
  };

  const handleSaveEdition = async (): Promise<void> => {
    if (montage) {
      await saveMontage();
      return;
    }

    if (!ready || !story) return;

    const source = preview.getSource();
    const subtitleElements = source.elements.filter((element: ElementSource) => (
      isSubtitleText(element.name)
    ));
    try {
      handleUpdateData({ blockSubtitles: false, loading: true });
      if (onPosted) {
        const updatedSubtitles = updateSubtitlesText(subtitleElements);
        const { subtitles } = await makePost<{ subtitles: Subtitle[] }>("/video/subtitles", {
          mediaId: story?.media?.id,
          subtitles: updatedSubtitles
        } as any);

        if (!subtitleElements.length && story?.media?.subtitles?.length) {
          onPosted({ ...story, media: { ...story.media, burnSubtitles, subtitles: [] } });
        } else if (subtitleElements.length) {
          onPosted({ ...story, media: { ...story.media, burnSubtitles, subtitles } });
          await handleAutosaveDraft({ burnSubtitles });
        }
      }

      const mediaData = getCreatomateMediaData(preview, burnSubtitles);
      if (onMediaEdited) {
        onMediaEdited(mediaData, selectedThumbnail);
        restartData();
      } else {
        await postEdition(mediaData);
        restartData();
        preview.dispose();
      }
      if (onThirdPartyMediasReload) onThirdPartyMediasReload(true);
      onClose();
    } catch (error) {
      errorService.error("An error occurred during save edition", { error, data: preview.getSource() });
    }
  };

  const saveDraft = async () => {
    await handleAutosaveDraft({ creatomateJson: preview.state });
    if (onThirdPartyMediasReload) onThirdPartyMediasReload(true);
    preview.dispose();
    onClose();
  };

  const handleOptionSelect = (selectedValue: string) => {
    switch (selectedValue) {
      case "save_draft":
        saveDraft();
        break;
      case "save_changes":
        handleSaveEdition();
        break;
      default:
        break;
    }
  };

  const isButtonDisabled = !!loading || !ready || uploadingMedia || !!pendingSubtitle
    || elements.length === 0;

  if (story?.id === undefined || story?.id === 0) {
    return (
      <Button
        disabled={
          loading || !ready || uploadingMedia
                || pendingSubtitle || elements.length === 0
                || (montage && !storyQuestionId)
        }
        onClick={handleSaveEdition}
      >
        <Icon icon="checkmark" />
        <Trans>{btnLabel}</Trans>
      </Button>
    );
  }

  return (
    <>
      <div className={`${isButtonDisabled ? "Download-Btn_disabled" : ""}`}>
        <Dropdown onChange={handleOptionSelect} selected="">
          <Dropdown.Button className={classnames("Download-Btn", "btn-dropdown")}>
            <Icon icon="checkmark" />
            <span className="Download-Text"><Trans>Save</Trans></span>
          </Dropdown.Button>
          <Dropdown.ListBox className="Download-Options">
            {story?.id !== 0 && (
              <Dropdown.Option className="Download-Option" value="save_draft">
                <Trans>Save draft</Trans>
              </Dropdown.Option>
            )}
            <Dropdown.Option className="Download-Option" value="save_changes">
              <Trans>{btnLabel}</Trans>
            </Dropdown.Option>
          </Dropdown.ListBox>
        </Dropdown>
      </div>
    </>
  );
};

const SaveDropdown: React.FC<DownloadDropdownProps> = ({
  onClose, preview
}) => {
  const { data } = useContext(CreatomateContext);
  const { pendingSubtitle, uploadingMedia, elements, activeElementsId } = data;
  const activeElement = elements.find(el => activeElementsId.includes(el.source.id));
  const mediaType = activeElement?.source?.type ?? "media";


  const tooltipData = () => {
    if (pendingSubtitle) return { display: true, message: "Please wait while the transcription is being processed" };
    if (data.montage && !data.storyQuestionId) return { display: true, message: "Please select a prompt in the ‘Details’ tab before saving" };
    if (uploadingMedia) return { display: true, message: `The ${mediaType} is being uploaded` };
    return { display: false, message: "" };
  };
  const tooltip = tooltipData();

  return (
    <>
      {tooltip.display ? (
        <Tooltip
          tip={
            <p className="Tooltip-uploaded">
              {tooltip.message}
            </p> as unknown as string
          }
        >
          <SaveDropdownElement onClose={onClose} preview={preview} />
        </Tooltip>
      ) : (
        <SaveDropdownElement onClose={onClose} preview={preview} />
      )}
    </>
  );
};

export default SaveDropdown;
